import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { SharedModule } from "../../../../shared/shared.module";
import { Widget } from "../../models/dashboard.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { WidgetDataFacadeService } from "../../services/widget-data-facade.service";
import { environment } from "../../../../../environments/environment";
import { Router } from "@angular/router";
import { format } from "date-fns";

export interface CertificatBioEcheanceItem {
  id: string;
  numero: string;
  organisme: string;
  dateValidite: Date;
  tiersId: string;
  tiersDenomination: string;
  joursRestants: number;
}

@Component({
  selector: "vl-widget-certificat-bio-echeance",
  standalone: true,
  imports: [SharedModule, WidgetTitleComponent],
  host: { class: "widget-component" },
  templateUrl: "./widget-certificat-bio-echeance.component.html",
  styleUrl: "./widget-certificat-bio-echeance.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCertificatBioEcheanceComponent {
  widget = input<Widget>();

  private http = inject(HttpClient);
  private widgetDataFacade = inject(WidgetDataFacadeService);
  private router = inject(Router);

  items = signal<CertificatBioEcheanceItem[]>([]);
  loading = signal(false);

  /** Only show certificates expiring within 90 days or already expired. */
  alertItems = computed(() =>
    this.items().filter((i) => i.joursRestants <= 90),
  );

  accentText = computed(() => {
    const alerts = this.alertItems();
    if (!alerts.length) return "";
    const expired = alerts.filter((i) => i.joursRestants <= 0).length;
    if (expired > 0) return `${expired} expiré${expired > 1 ? "s" : ""}`;
    return `${alerts.length} à renouveler`;
  });

  constructor() {
    effect(
      async () => {
        this.loading.set(true);
        try {
          const data = await firstValueFrom(
            this.http.get<CertificatBioEcheanceItem[]>(
              `${environment.api()}/certificat_bio/echeances`,
            ),
          );
          this.items.set(data);
        } finally {
          this.loading.set(false);
        }
      },
      { allowSignalWrites: true },
    );

    // Refresh on dashboard "Actualiser" click
    let firstTick = true;
    effect(
      async () => {
        const tick = this.widgetDataFacade.refreshTick();
        if (firstTick) {
          firstTick = false;
          return;
        }
        void tick;
        this.loading.set(true);
        try {
          const data = await firstValueFrom(
            this.http.get<CertificatBioEcheanceItem[]>(
              `${environment.api()}/certificat_bio/echeances`,
            ),
          );
          this.items.set(data);
        } finally {
          this.loading.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  statusClass(jours: number): string {
    if (jours <= 0) return "on-error";
    if (jours <= 30) return "on-warning";
    if (jours <= 90) return "on-warning2";
    return "on-valid";
  }

  statusLabel(item: CertificatBioEcheanceItem): string {
    if (item.joursRestants <= 0) {
      return `Expiré depuis le ${format(new Date(item.dateValidite), "dd/MM/yyyy")}`;
    }
    return `Expire dans ${item.joursRestants}j`;
  }

  goToTiers(tiersId: string) {
    this.router.navigate(["/tiers/id", tiersId]);
  }
}
